/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { faCirclePlay } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import RemainderFilter from './RemainderFilter';
import RemainderTable from './RemainderTable';

import styles from './styles.module.scss';
import { useAppContext } from 'providers/AppProvider';
import Button from 'components/base/Button';
import ButtonRefresh from 'components/base/ButtonRefresh';
import queryClient from 'service/queryClient';

export const remainderBreadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'reports',
    url: '/reports/remainder'
  },

  {
    label: 'remainder',
    active: true
  }
];

const Remainder = () => {
  const { t, i18n } = useTranslation();
  const {
    config: { theme }
  } = useAppContext();
  const currencyName = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency?.name?.[i18n?.language]
  );
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const [generateTableData, setGeneratedTableData] = useState([]);
  const [savedValues, setSavedValues] = useState([]);
  const [getName, setGetName] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(getName);
  const [getBarcode, setGetBarcode] = useState('');
  const [getFilterData, setGetFilterData] = useState({});
  const [selectedCurrencyName, setCurrencyName] = useState('');
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [totalIncome, setTotalincome] = useState(0);
  const [totalSale, setTotalSale] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [debouncedBarcode, setDebouncedBarcode] = useState('');
  const [labeledVisibleFields, setLabeledVisibleFields] = useState<string>('');

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(getName);
    }, 400);

    return () => clearTimeout(handler);
  }, [getName]);

  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
      setTimeout(() => barcodeInputRef.current?.select(), 500);
    }
  }, []);

  const telegramLink = 'https://t.me/c/2754785074/15';

  const handleClick = () => {
    window.open(telegramLink, '_blank');
  };

  return (
    <div>
      <PageBreadcrumb items={remainderBreadcrumbItems} />
      <div>
        <div className="d-flex gap-2 align-items-center mb-5">
          <h2>{t('remainder')}</h2>

          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip style={{ position: 'fixed' }}>
                {t('video_lessons')}
              </Tooltip>
            }
          >
            <div className="text-center">
              <Button
                variant="link"
                className="p-0"
                onClick={() => handleClick()}
              >
                <FontAwesomeIcon icon={faCirclePlay} className="fs-7" />
              </Button>
            </div>
          </OverlayTrigger>
          <ButtonRefresh
            onRefresh={() => {
              queryClient.refetchQueries('GET_REPORTS_REMAINING');
            }}
          />
        </div>
        <RemainderFilter
          setGeneratedTableData={setGeneratedTableData}
          savedValues={savedValues}
          setSavedValues={setSavedValues}
          setGetName={setGetName}
          setGetBarcode={setGetBarcode}
          setGetFilterData={setGetFilterData}
          getName={getName}
          getBarcode={getBarcode}
          setCurrencyName={setCurrencyName}
          setTotalQuantity={setTotalQuantity}
          setTotalincome={setTotalincome}
          setTotalSale={setTotalSale}
          currentPage={currentPage}
          setPageCount={setPageCount}
          getFilterData={getFilterData}
          barcodeInputRef={barcodeInputRef}
          setDebouncedBarcode={setDebouncedBarcode}
          setCurrentPage={setCurrentPage}
          labeledVisibleFields={labeledVisibleFields}
        />
        <RemainderTable
          generateTableData={generateTableData}
          setGeneratedTableData={setGeneratedTableData}
          getName={getName}
          debouncedSearchValue={debouncedSearchValue}
          getBarcode={getBarcode}
          getFilterData={getFilterData}
          savedValues={savedValues}
          setTotalQuantity={setTotalQuantity}
          setTotalincome={setTotalincome}
          setTotalSale={setTotalSale}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageCount={pageCount}
          setPageCount={setPageCount}
          // barcodeInputRef={barcodeInputRef}
          debouncedBarcode={debouncedBarcode}
          setLabeledVisibleFields={setLabeledVisibleFields}
        />

        <div
          className={
            theme === 'dark' ? styles.darkFooterRemain : styles.footerRemain
          }
        >
          <div className={styles.box}>
            <p className="mb-0" style={{ fontWeight: '800' }}>
              {t('total_quantity')}:
            </p>
            <span style={{ fontWeight: '800' }}>
              {totalQuantity
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(totalQuantity)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className={styles.box}>
            <p className="mb-0" style={{ fontWeight: '800' }}>
              {t('total_amount_receipts')}: (
              {selectedCurrencyName !== ''
                ? selectedCurrencyName
                : currencyName}
              )
            </p>
            <span style={{ fontWeight: '800' }}>
              {totalIncome
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(totalIncome)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>

          <div className={styles.box}>
            <p className="mb-0" style={{ fontWeight: '800' }}>
              {t('total_sale_amount')}: (
              {selectedCurrencyName !== ''
                ? selectedCurrencyName
                : currencyName}
              )
            </p>
            <span style={{ fontWeight: '800' }}>
              {totalSale
                ? new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                    useGrouping: true
                  })
                    .format(totalSale)
                    .replace(/,/g, ' ')
                : 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Remainder;
